/**
 * Counts for the agent inbox header strip (SPEC §9).
 *
 * Wraps GET /api/v1/agent/tickets/counts. The response includes per-
 * status counts plus an `assigned_to_me` and `all_active` aggregate the
 * inbox header copy uses ("X open across all tickets, Y assigned to
 * you").
 *
 * Polls on the same 30s heartbeat as ``useAgentTickets`` so the
 * "12 open across all tickets, 3 assigned to you" subtitle stays in
 * sync with the row list below it. See that hook's comment for the
 * cadence rationale.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type { TicketCountsResponse } from "@/types/api";

const AGENT_INBOX_POLL_MS = 30_000;

export const agentTicketCountsQueryKey = ["agent", "tickets", "counts"] as const;

export function useAgentTicketCounts(): UseQueryResult<TicketCountsResponse> {
  return useQuery({
    queryKey: agentTicketCountsQueryKey,
    queryFn: async () => {
      const response = await api.get<TicketCountsResponse>(
        "/agent/tickets/counts",
      );
      return response.data;
    },
    refetchInterval: AGENT_INBOX_POLL_MS,
    refetchOnWindowFocus: true,
  });
}
